import React, { useState, useEffect } from 'react';
import { useTranslation  } from 'react-i18next';
import classNames from 'classnames';
import _ from 'lodash';

import Button from 'src/components/Button/Button';
import Icon from 'src/components/Icon/Icon';
import { Customer } from 'src/interfaces/AuthState';
import './BankTransfer.scss';
import useIsMobile from 'src/hooks/useIsMobile';
import { useDispatch } from 'react-redux';

interface BankTransferProps{
  customer: Customer,
  withdraw?: boolean,
  activeWallet: any
}

function BankTransfer({ customer, withdraw=false, activeWallet } : BankTransferProps) {
  const [showBankDetails, setShowBankDetails] = useState(true);
  const [amount, setAmount] = useState('0.00');
  const [showBankAccounts, setShowBankAccounts] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [userInfo, setUserInfo] = useState(
    {
      accountId: 0,
      accountName: '',
      bankAccount: '',
      bankName: '',
      swiftCode: '',
      currency: '',
      bankAddress: ''
    }
  );
  const [bankAccounts, setBankAccounts] = useState([]);

  const { t } = useTranslation();
  const mobile = useIsMobile();

  const dispatch = useDispatch();

  useEffect(() => {
    if (customer){

      setUserInfo({
        ...userInfo,
        accountId: customer.accountNumber,
        accountName: `${ customer.firstName } ${ customer.lastName }`
      });

      setBankAccounts([]);
    }
  }, [customer]);

  const handleChange = (e) => {
    e.preventDefault();

    setUserInfo({
      ...userInfo,
      [e.target.name]: e.target.value
    });
  };

  const amountChange = (e) => {
    e.preventDefault();

    setAmount(e.target.value);
  };

  const handleSelectionChange = (e) => {
    e.preventDefault();

    setUserInfo({
      ...userInfo,
      bankAccount: e.target.value
    });

    const filteredBankAccounts = _.filter([], (account) => {
      if (account.name.includes(e.target.value) || account.number.includes(e.target.value)){
        return account;
      }
      return null;
    });

    if (filteredBankAccounts.length === 0){
      setShowBankAccounts(false);
    } else if (filteredBankAccounts.length > 0 && !showBankAccounts){
      setShowBankAccounts(true);
    }

    setBankAccounts(_.compact(filteredBankAccounts));
  };

  const handleSelectClick = (number, name) => (e) => {
    e.preventDefault();

    setUserInfo({
      ...userInfo,
      bankAccount: number,
      bankName: name
    });
    setShowBankAccounts(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (withdraw){
      dispatch.account.requestWithdrawal({ ...userInfo, amount: +amount, instructions, currency: activeWallet.currency  });
      setAmount('0.00');
    }
  };

  return (
    <form action="submit" onSubmit={ handleSubmit }>
      <div className="bank-transfer">
        <div className="bank-transfer__multiple-container">
          <div className="bank-transfer__double-container">
            <div className="bank-transfer__label-input bank-transfer__label-input--double">
              <label className="bank-transfer__label bank-transfer__label--double">{ t('depositFunds.amount') }</label>
              <input
                className="bank-transfer__input bank-transfer__input"
                value={ amount }
                required
                type="number"
                name="amount"
                onChange={ amountChange }
              />
            </div>
            <div className="bank-transfer__label-input bank-transfer__label-input">
              <label className="bank-transfer__label bank-transfer__label">{ t('depositFunds.currency') }</label>
              <span className="bank-transfer__currency" id="currency" >{ activeWallet?.currency }</span>
            </div>
          </div>
          <div className={
            classNames({
              'bank-transfer__label-input': true,
              'bank-transfer__label-input--multiple': !mobile
            })
          }
          >
            <label className="bank-transfer__label">{ t('depositFunds.transactionReference') }</label>
            <input
              type="text"
              className="bank-transfer__input"
              onChange={ handleChange }
              name="accountId"
              value={ userInfo.accountId }
              placeholder={ t('depositFunds.transactionReferencePlaceholder') }
            />
          </div>
          <div className={
            classNames({
              'bank-transfer__label-input': true,
              'bank-transfer__label-input--multiple': !mobile
            })
          }
          >
            <label className="bank-transfer__label">{ t('depositFunds.accountName') }</label>
            <input
              type="text"
              name="accountName"
              placeholder={ t('depositFunds.accountNamePlaceholder') }
              className="bank-transfer__input"
              value={ userInfo.accountName }
              onChange={ handleChange }
            />
          </div>
          <div className={
            classNames({
              'bank-transfer__label-input': true,
              'bank-transfer__label-input--multiple': !mobile
            })
          }
          >
            <label className="bank-transfer__label">{ t('depositFunds.bankAccount') }</label>
            <div className="bank-transfer__search-container">
              <input
                type="text"
                name="bankAccount"
                onChange={ handleSelectionChange }
                placeholder= { t('depositFunds.bankAccountPlaceholder') }
                className="bank-transfer__input"
                autoComplete="off"
                value={ userInfo.bankAccount }
                onFocus={ () => setShowBankAccounts(true) }
                onBlur={ () => setShowBankAccounts(false) }
              />
              {
                customer.bankAccounts &&
              <Icon
                icon="dropdownArrow"
                onClick= { () => setShowBankAccounts(!showBankAccounts) }
                className={
                  classNames({
                    'bank-transfer__select-icon': true,
                    'bank-transfer__select-icon--open': showBankAccounts
                  })
                }
              />
              }
            </div>
            {
              showBankAccounts && <div className="bank-transfer__bank-list">
                <ul className="bank-transfer__list-container">
                  {
                    _.map(bankAccounts, (account) => {
                      return (
                        <li
                          key= { account.number }
                          className="bank-transfer__list-item"
                          onClick={ handleSelectClick(account.number, account.name) }
                        >
                          <div className="bank-transfer__account-number">{ account.number }</div>
                          <div className="bank-transfer__account-name">{ account.name }</div>
                        </li>);
                    })
                  }
                </ul>
              </div>
            }
          </div>
        </div>
        <div className="bank-transfer__bank-details" onClick={ () => setShowBankDetails(!showBankDetails) }>
          <span>{ t('depositFunds.bankDetails') }</span>
          <Icon
            icon="dropdownArrow"
            className={
              classNames({
                'bank-transfer__dropdown-icon': true,
                'bank-transfer__dropdown-icon--open': showBankDetails
              })
            }
          />
        </div>
        {
          showBankDetails && (
            <>
              <div className="bank-transfer__multiple-container">
                <div className={
                  classNames({
                    'bank-transfer__label-input': true,
                    'bank-transfer__label-input--multiple': !mobile
                  })
                }
                >
                  <label className="bank-transfer__label">{ t('depositFunds.bankName') }</label>
                  <input
                    type="text"
                    name="bankName"
                    value={ userInfo.bankName }
                    className="bank-transfer__input"
                    onChange={ handleChange }
                    placeholder={ t('depositFunds.bankNamePlaceholder') }
                  />
                </div>
                <div className={
                  classNames({
                    'bank-transfer__label-input': true,
                    'bank-transfer__label-input--multiple': !mobile
                  })
                }
                >
                  <label className="bank-transfer__label">{ t('depositFunds.swiftCode') }</label>
                  <input
                    type="text"
                    name="swiftCode"
                    value={ userInfo.swiftCode }
                    className="bank-transfer__input"
                    onChange={ handleChange }
                    placeholder={ t('depositFunds.swiftCodePlaceholder') }
                  />
                </div>
                <div className={
                  classNames({
                    'bank-transfer__label-input': true,
                    'bank-transfer__label-input--multiple': !mobile
                  })
                }
                >
                  <label className="bank-transfer__label">{ t('depositFunds.accountCurrency') }</label>
                  <input
                    type="text"
                    name="currency"
                    value={ userInfo.currency }
                    className="bank-transfer__input"
                    onChange={ handleChange }
                    placeholder={ t('depositFunds.currencyPlaceholder') }
                  />
                </div>
                <div className={
                  classNames({
                    'bank-transfer__label-input': true,
                    'bank-transfer__label-input--multiple': !mobile
                  })
                }
                >
                  <label className="bank-transfer__label">{ t('depositFunds.bankAddress') }</label>
                  <input
                    type="text"
                    name="bankAddress"
                    value={ userInfo.bankAddress }
                    className="bank-transfer__input"
                    onChange={ handleChange }
                    placeholder={ t('depositFunds.bankAddressPlaceholder') }
                  />
                </div>
              </div>
              <div className="bank-transfer__single-container">
                <label className="bank-transfer__label">{ t('depositFunds.additionalInstructions') }</label>
                <input
                  type="text"
                  value={ instructions }
                  name="instructions"
                  className="bank-transfer__input"
                  onChange={ (e) => { setInstructions(e.target.value); } }
                  placeholder={ t('depositFunds.additionalDetailsPlaceholder') }
                />
              </div>
            </>
          )
        }
        <Button
          className="bank-transfer__submit btn btn--cta"
          type="submit"
          value="Request"
          text={ !withdraw ? 'Submit' : 'Request' }
          disabled={ _.some(userInfo, (info) => info === '' || info === null) || +amount === 0 || instructions === '' }
        />
      </div>
    </form>
  );
}

export default BankTransfer;
